/**
 * Un fichier d'agenda, pour que l'échéance sorte de la page.
 *
 * LE VISITEUR REPART AVEC QUELQUE CHOSE. Une date lue sur un site est oubliée
 * le soir même ; la même date dans son agenda le rappellera dans huit mois,
 * quand il aura oublié jusqu'au nom du site. C'est la seule façon d'être utile
 * à quelqu'un qui ne veut ni compte ni adresse e-mail.
 *
 * DEUX RÈGLES DU FORMAT QUE PERSONNE NE VOIT VENIR. Les virgules, les
 * points-virgules et les antislashs doivent être échappés, sinon la ligne est
 * coupée au mauvais endroit et l'événement perd sa fin. Et une ligne ne peut
 * pas dépasser soixante-quinze octets : au-delà, elle se plie avec une espace
 * en tête de la suivante. Un accent compte pour deux octets, ce qui arrive
 * vite en français : le pliage se fait donc sur les octets, jamais sur les
 * caractères, sans quoi le fichier casse au premier « é » mal placé.
 */

/** Les caractères que le format réserve, et qu'il faut donc protéger. */
function echapper(texte) {
    return String(texte)
        .replace(/\\/g, "\\\\")
        .replace(/;/g, "\\;")
        .replace(/,/g, "\\,")
        .replace(/\r?\n/g, "\\n");
}

/** Une date au format du calendrier : AAAAMMJJ, sans heure ni fuseau. */
function dateSeule(date) {
    const mois = String(date.getMonth() + 1).padStart(2, "0");
    const jour = String(date.getDate()).padStart(2, "0");
    return `${date.getFullYear()}${mois}${jour}`;
}

function horodatage(date) {
    return `${dateSeule(date)}T${String(date.getUTCHours()).padStart(2, "0")}0000Z`;
}

/**
 * Plie une ligne à soixante-quinze octets, en comptant en octets et non en
 * caractères : « é » en vaut deux, et une coupure au milieu casserait tout.
 */
function plier(ligne) {
    const encodeur = new TextEncoder();
    const octets = encodeur.encode(ligne);
    if (octets.length <= 75) return ligne;

    const morceaux = [];
    let courant = "";
    let taille = 0;
    // La ligne de continuation commence par une espace, qui compte elle aussi.
    let limite = 75;
    for (const caractere of ligne) {
        const poids = encodeur.encode(caractere).length;
        if (taille + poids > limite) {
            morceaux.push(courant);
            courant = "";
            taille = 1;
            limite = 74;
        }
        courant += caractere;
        taille += poids;
    }
    if (courant) morceaux.push(courant);
    return morceaux.join("\r\n ");
}

/**
 * Le contenu d'un fichier .ics.
 *
 * @param evenements  [{ cle, date, titre, description, rappelJours }]
 * @param nom         le nom du calendrier, tel que l'agenda l'affichera.
 */
export function construireCalendrier({ evenements, nom, domaine = "believemy.com" }) {
    const maintenant = new Date();
    const lignes = [
        "BEGIN:VCALENDAR",
        "VERSION:2.0",
        `PRODID:-//Believemy//Outils//FR`,
        "CALSCALE:GREGORIAN",
        "METHOD:PUBLISH",
        `X-WR-CALNAME:${echapper(nom)}`,
    ];

    for (const evenement of evenements) {
        const debut = evenement.date;
        // Un événement d'une journée se termine le lendemain : c'est la borne
        // de fin exclusive du format, et l'oublier affiche deux jours pleins.
        const fin = new Date(debut);
        fin.setDate(fin.getDate() + 1);

        lignes.push(
            "BEGIN:VEVENT",
            `UID:${evenement.cle}-${dateSeule(debut)}@${domaine}`,
            `DTSTAMP:${horodatage(maintenant)}`,
            `DTSTART;VALUE=DATE:${dateSeule(debut)}`,
            `DTEND;VALUE=DATE:${dateSeule(fin)}`,
            plier(`SUMMARY:${echapper(evenement.titre)}`),
        );
        if (evenement.description) {
            lignes.push(plier(`DESCRIPTION:${echapper(evenement.description)}`));
        }
        if (evenement.rappelJours) {
            lignes.push(
                "BEGIN:VALARM",
                `TRIGGER:-P${evenement.rappelJours}D`,
                "ACTION:DISPLAY",
                plier(`DESCRIPTION:${echapper(evenement.titre)}`),
                "END:VALARM",
            );
        }
        lignes.push("END:VEVENT");
    }

    lignes.push("END:VCALENDAR");
    return lignes.join("\r\n");
}

/** Propose le fichier au téléchargement, sans quitter la page. */
export function telechargerCalendrier(contenu, nomFichier) {
    const blob = new Blob([contenu], { type: "text/calendar;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const lien = document.createElement("a");
    lien.href = url;
    lien.download = nomFichier;
    document.body.appendChild(lien);
    lien.click();
    document.body.removeChild(lien);
    // Le navigateur garde l'objet tant qu'on ne le révoque pas.
    setTimeout(() => URL.revokeObjectURL(url), 1000);
}
